import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useLayoutSlots } from '@app/layouts/layoutContext';
import { useAppStore } from '@app/store/useAppStore';
import { useKey } from '@app/hooks/useKey';
import { KeyModeSelector } from '@components/KeyModeSelector';
import { ByChordPicker } from '@components/ByChordPicker';
import { RelativeKeyBadge } from '@components/RelativeKeyBadge';
import { Switch } from '@components/Switch';
import styles from './KeyRoute.module.css';

type PickerMode = 'full' | 'byChord';

export function KeyRoute() {
  const { setCenter, setRight } = useLayoutSlots();
  const [mode, setMode] = useState<PickerMode>('full');
  const navigate = useNavigate();

  const keyId = useAppStore((s) => s.selectedKeyId);
  const selectKeyAndMode = useAppStore((s) => s.selectKeyAndMode);
  const key = useKey(keyId);
  const useTraditional = useAppStore((s) => s.useTraditionalNames);
  const setUseTraditional = useAppStore((s) => s.setUseTraditionalNames);

  const handleByChordSelect = useCallback(
    (id: string) => {
      // ByChordPicker chips advertise concrete major/minor keys; sync the mode so
      // the displayed key doesn't desync (e.g. tapping "Am" while in E Phrygian).
      selectKeyAndMode(id);
      navigate('/chords');
    },
    [selectKeyAndMode, navigate],
  );

  useEffect(() => {
    setCenter(
      <div className={styles.center}>
        <div className={styles.modeToggle} role="tablist" aria-label="Key picker mode">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'full'}
            className={mode === 'full' ? styles.active : undefined}
            onClick={() => setMode('full')}
          >
            Full Circle
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'byChord'}
            className={mode === 'byChord' ? styles.active : undefined}
            onClick={() => setMode('byChord')}
          >
            By Chord
          </button>
        </div>

        {mode === 'full' ? (
          <KeyModeSelector />
        ) : (
          <ByChordPicker onSelectKey={handleByChordSelect} />
        )}
      </div>,
    );
    setRight(
      <div className={styles.info}>
        {key ? (
          <>
            <h2 className={styles.keyName}>{key.displayName}</h2>
            <div className={styles.notes}>{key.notes.join(' · ')}</div>
            <RelativeKeyBadge root={key.relativeKey.root} quality={key.relativeKey.quality} />
            <Switch
              label="Use traditional chord names"
              checked={useTraditional}
              onChange={setUseTraditional}
            />
            <Link to="/chords" className={styles.cta}>
              Explore chords →
            </Link>
          </>
        ) : (
          <p className={styles.empty}>Pick a key on the Circle of Fifths.</p>
        )}
      </div>,
    );
    return () => {
      setCenter(null);
      setRight(null);
    };
  }, [mode, key, useTraditional, setCenter, setRight, setUseTraditional, handleByChordSelect]);

  return null;
}
